/** Resolve AR/AP linkage and description for transactions posted from receivables/payables. */

function str(v: unknown): string | undefined {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s || undefined;
}

function pick(...values: unknown[]): string | undefined {
  for (const v of values) {
    const s = str(v);
    if (s) return s;
  }
  return undefined;
}

/** Backend internal linkage token — not for display. */
const AR_AP_INTERNAL_REF_RE =
  /ar_ap:(receivable|payable):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export function parseArApInternalReference(value: string | undefined): {
  source: 'receivable' | 'payable';
  entryId: string;
} | null {
  if (!value) return null;
  const match = value.trim().match(AR_AP_INTERNAL_REF_RE);
  if (!match?.[1] || !match[2]) return null;
  const kind = match[1].toLowerCase();
  if (kind !== 'receivable' && kind !== 'payable') return null;
  return { source: kind, entryId: match[2] };
}

export function isArApInternalReference(value: string | undefined): boolean {
  return parseArApInternalReference(value) !== null;
}

/** e.g. `other_expense · ar_ap:payable:uuid` — system-generated, not user memo. */
export function descriptionLooksLikeInternalArApLine(desc: string | undefined): boolean {
  if (!desc?.trim()) return false;
  return AR_AP_INTERNAL_REF_RE.test(desc);
}

function nestedArApEntry(t: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!t) return undefined;
  if (t.receivable && typeof t.receivable === 'object') {
    return t.receivable as Record<string, unknown>;
  }
  if (t.receivable_entry && typeof t.receivable_entry === 'object') {
    return t.receivable_entry as Record<string, unknown>;
  }
  if (t.payable && typeof t.payable === 'object') {
    return t.payable as Record<string, unknown>;
  }
  if (t.payable_entry && typeof t.payable_entry === 'object') {
    return t.payable_entry as Record<string, unknown>;
  }
  return undefined;
}

export function resolveArApContactName(t: Record<string, unknown>): string | undefined {
  const entry = nestedArApEntry(t);
  return pick(
    entry?.contact_name,
    entry?.contactName,
    t.contact_name,
    t.contactName,
    typeof t.contact === 'object' && t.contact
      ? (t.contact as Record<string, unknown>).name
      : undefined,
    t.vendor_name,
    t.vendorName,
    t.client_name,
    t.clientName,
    typeof t.vendor === 'string' ? t.vendor : undefined,
    typeof t.client === 'string' ? t.client : undefined
  );
}

/** User-facing invoice/bill ref — hides internal ar_ap tokens. */
export function sanitizeDisplayReference(
  ref: string | undefined,
  t?: Record<string, unknown>
): string | undefined {
  const raw = str(ref);
  if (!raw) return undefined;
  if (!isArApInternalReference(raw)) return raw;

  const entry = nestedArApEntry(t);
  const humanRef = pick(entry?.reference_number, entry?.referenceNumber);
  if (humanRef && !isArApInternalReference(humanRef)) return humanRef;
  return undefined;
}

function linkFromInternalToken(t: Record<string, unknown>): {
  receivable_id?: string;
  payable_id?: string;
  ar_ap_source?: 'receivable' | 'payable';
} {
  const tokenSource = pick(t.reference_number, t.reference, t.description, t.memo);
  const parsed = parseArApInternalReference(tokenSource);
  if (!parsed) return {};
  if (parsed.source === 'receivable') {
    return { receivable_id: parsed.entryId, ar_ap_source: 'receivable' };
  }
  return { payable_id: parsed.entryId, ar_ap_source: 'payable' };
}

export function isArApSourcedTransactionRecord(t: Record<string, unknown>): boolean {
  const sourceType = str(t.source_type ?? t.sourceType);
  const sourceObj = t.source as Record<string, unknown> | undefined;
  const fromToken = linkFromInternalToken(t);
  return Boolean(
    t.receivable_id ??
      t.receivableId ??
      t.payable_id ??
      t.payableId ??
      fromToken.ar_ap_source ??
      (sourceType === 'receivable' || sourceType === 'payable') ??
      (sourceObj?.type === 'receivable' || sourceObj?.type === 'payable') ??
      descriptionLooksLikeInternalArApLine(str(t.description)) ??
      descriptionLooksLikeInternalArApLine(str(t.memo))
  );
}

export function resolveArApTransactionLinkIds(t: Record<string, unknown>): {
  receivable_id?: string;
  payable_id?: string;
  ar_ap_source?: 'receivable' | 'payable';
} {
  const receivable_id = pick(t.receivable_id, t.receivableId);
  const payable_id = pick(t.payable_id, t.payableId);
  const sourceType = pick(t.source_type, t.sourceType, (t.source as Record<string, unknown> | undefined)?.type);

  if (receivable_id) {
    return { receivable_id, ar_ap_source: 'receivable' };
  }
  if (payable_id) {
    return { payable_id, ar_ap_source: 'payable' };
  }

  const fromToken = linkFromInternalToken(t);
  if (fromToken.ar_ap_source) return fromToken;

  if (sourceType === 'receivable') {
    return { ar_ap_source: 'receivable' };
  }
  if (sourceType === 'payable') {
    return { ar_ap_source: 'payable' };
  }
  return {};
}

/** Description stored on the parent receivable/payable entry or payment note. */
export function resolveArApSourceDescription(t: Record<string, unknown>): string | undefined {
  const entry = nestedArApEntry(t);
  const payment =
    t.payment && typeof t.payment === 'object' ? (t.payment as Record<string, unknown>) : undefined;
  const meta = t.meta && typeof t.meta === 'object' ? (t.meta as Record<string, unknown>) : undefined;

  const candidate = pick(
    t.ar_ap_description,
    t.arApDescription,
    t.source_description,
    t.sourceDescription,
    entry?.description,
    payment?.description,
    meta?.ar_ap_description,
    meta?.source_description
  );

  if (candidate && !descriptionLooksLikeInternalArApLine(candidate)) {
    return candidate;
  }
  return undefined;
}

export function descriptionLooksLikeContactOnly(
  desc: string,
  contactName: string | undefined
): boolean {
  if (!contactName) return false;
  return desc.trim().toLowerCase() === contactName.trim().toLowerCase();
}

export function descriptionLooksLikeCategoryToken(desc: string, categoryRaw: string | undefined): boolean {
  const cat = (categoryRaw ?? '').trim().toLowerCase();
  if (!cat || cat === '—' || cat === '-') return false;
  const d = desc.trim().toLowerCase();
  if (d === cat || d === cat.replace(/_/g, ' ') || d === cat.replace(/-/g, ' ')) {
    return true;
  }
  if (descriptionLooksLikeInternalArApLine(desc)) {
    const slugPart = d.split('·')[0]?.trim();
    if (slugPart === cat || slugPart === cat.replace(/_/g, ' ')) return true;
  }
  return false;
}

/** Description from parent receivable/payable only — no synthetic "Paid — category" text. */
export function resolveArApOnlyDescription(t: Record<string, unknown>): string | undefined {
  return resolveArApSourceDescription(t);
}

/** @deprecated Use resolveArApOnlyDescription; no synthetic fallbacks. */
export function buildArApSyntheticDescription(
  _t: Record<string, unknown>,
  _contactName: string | undefined
): string | undefined {
  return undefined;
}

export type ArApListDeepLinkStatus = 'open' | 'settled' | 'all';

/**
 * Direction of the AR/AP ↔ transaction relationship for UI labels.
 * - `originates_entry`: unpaid expense / not-received income created the payable/receivable
 * - `from_entry`: payment settlement (or posting) created/linked this transaction from AR/AP
 */
export function resolveArApLinkRelation(
  transaction: Pick<
    {
      payment_status?: string;
      type?: string;
    },
    'payment_status' | 'type'
  >
): 'originates_entry' | 'from_entry' {
  const status = String(transaction.payment_status ?? '')
    .trim()
    .toLowerCase();
  if (status === 'unpaid' || status === 'not_received') {
    return 'originates_entry';
  }
  return 'from_entry';
}

export function arApSourceLinkLabel(
  source: 'receivable' | 'payable',
  relation: 'originates_entry' | 'from_entry'
): string {
  if (relation === 'originates_entry') {
    return source === 'receivable' ? 'Receivable' : 'Payable';
  }
  return source === 'receivable' ? 'From receivable' : 'From payable';
}

/** List status card to open so the detail is visible in the matching bucket. */
export function arApListStatusForLink(
  relation: 'originates_entry' | 'from_entry',
  entryStatus?: string
): ArApListDeepLinkStatus {
  const normalized = String(entryStatus ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'settled' || normalized === 'cancelled') return 'settled';
  if (
    normalized === 'pending' ||
    normalized === 'partial' ||
    normalized === 'overdue'
  ) {
    return 'open';
  }
  // Fall back from transaction relation when entry status is unknown
  return relation === 'originates_entry' ? 'open' : 'settled';
}

export function arApReceivablesPayablesHref(
  source: 'receivable' | 'payable',
  entryId?: string,
  listStatus: ArApListDeepLinkStatus = 'settled'
): string {
  const tab = source === 'receivable' ? 'receive' : 'pay';
  const params = new URLSearchParams({ tab, status: listStatus });
  if (entryId) params.set('detail', entryId);
  return `/receivables-payables?${params.toString()}`;
}
